import React from 'react';

interface IColorSwatchProps {
  color: string;
  size?: number;
  borderRadius?: number;
  className?: string;
  showBorder?: boolean;
}

const TRANSPARENT_HEX_REGEX = /^#?[0-9a-fA-F]{8}$/;
let colorSwatchPatternIdSeed = 0;

/**
 * ColorSwatch：统一渲染颜色面板中的小色块（SVG 版本）。
 * - 支持普通色块渲染；
 * - 支持透明色（`#RRGGBBAA` 且 AA 为 `00`）的棋盘格渲染；
 * - 可通过 showBorder 控制是否展示描边，避免在浅色背景中"隐形"。
 */
const ColorSwatch: React.FC<IColorSwatchProps> = (props) => {
  const { color, size = 20, borderRadius = 2, className, showBorder = false } = props;
  const normalizedColor = color.toLowerCase();
  const isTransparentSwatch =
    TRANSPARENT_HEX_REGEX.test(normalizedColor) && normalizedColor.endsWith('00');

  const hasBorder = showBorder || isTransparentSwatch;
  const strokeWidth = hasBorder ? 1 : 0;
  const rectInset = strokeWidth / 2;
  const rectSize = size - strokeWidth;
  const rectRx = hasBorder ? Math.max(0, borderRadius - rectInset) : borderRadius;
  const rectBorderColor = hasBorder ? '#D8DAE4' : 'none';

  const patternIdRef = React.useRef<string>('');
  if (!patternIdRef.current) {
    // 每个实例持有独立 pattern id，避免同页多个 SVG 引用冲突。
    colorSwatchPatternIdSeed += 1;
    patternIdRef.current = `dbl-color-swatch-pattern-${colorSwatchPatternIdSeed}`;
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
    >
      {isTransparentSwatch ? (
        <>
          <defs>
            <pattern
              id={patternIdRef.current}
              width="8"
              height="8"
              patternUnits="userSpaceOnUse"
            >
              <rect width="8" height="8" fill="#FFFFFF" />
              <path d="M0 0H4V4H0V0ZM4 4H8V8H4V4Z" fill="#D8DAE4" />
            </pattern>
          </defs>
          <rect
            x={rectInset}
            y={rectInset}
            width={rectSize}
            height={rectSize}
            rx={rectRx}
            fill={`url(#${patternIdRef.current})`}
            stroke={rectBorderColor}
            strokeWidth={strokeWidth}
          />
        </>
      ) : (
        <rect
          x={rectInset}
          y={rectInset}
          width={rectSize}
          height={rectSize}
          rx={rectRx}
          fill={color}
          stroke={rectBorderColor}
          strokeWidth={strokeWidth}
        />
      )}
    </svg>
  );
};

export type { IColorSwatchProps };
export default React.memo(ColorSwatch);
